import { projectMap } from "../data/projects.js";
import { SCENE_STATES } from "../data/sceneStates.js";

export class UIController {
  constructor({ bridge }) {
    this.bridge = bridge;

    this.app = document.getElementById("app");
    this.loader = document.getElementById("loader");
    this.loaderProgress = document.getElementById("loader-progress");
    this.loaderText = document.getElementById("loader-text");
    this.errorOverlay = document.getElementById("error-overlay");
    this.errorDismiss = document.getElementById("error-dismiss");
    this.qualityBadge = document.getElementById("quality-badge");

    this.panels = Array.from(document.querySelectorAll("[data-panel]"));
    this.projectDetail = document.getElementById("project-detail");
    this.projectCategory = document.getElementById("project-category");
    this.projectTitle = document.getElementById("project-title");
    this.projectTeaser = document.getElementById("project-teaser");
    this.projectBody = document.getElementById("project-body");
    this.projectLink = document.getElementById("project-link");

    this.contactForm = document.getElementById("contactFormNew");
    this.contactStatus = document.getElementById("contact-status");
    this.contactSubmit = this.contactForm?.querySelector("button[type='submit']") ?? null;

    this.currentState = SCENE_STATES.BOOT;

    this.bindDomEvents();
    this.bridge.on("CONTACT_RESULT", (event) => this.onContactResult(event.detail));
  }

  bindDomEvents() {
    document.querySelectorAll("[data-nav-target]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        const { navTarget } = event.currentTarget.dataset;
        if (!navTarget) {
          return;
        }
        this.bridge.emit("NAVIGATE", { state: navTarget });
      });
    });

    document.querySelectorAll("[data-project-open]").forEach((button) => {
      button.addEventListener("click", (event) => {
        const slug = event.currentTarget.dataset.projectOpen;
        this.bridge.emit("OPEN_PROJECT", { slug });
      });
    });

    document.querySelectorAll("[data-project-close]").forEach((button) => {
      button.addEventListener("click", () => {
        this.bridge.emit("CLOSE_PROJECT", {});
      });
    });

    if (this.errorDismiss) {
      this.errorDismiss.addEventListener("click", () => {
        this.hideLoadError();
      });
    }

    window.addEventListener("popstate", () => {
      const state = window.location.pathname.endsWith("/new/datenschutz")
        ? SCENE_STATES.LEGAL
        : SCENE_STATES.INTRO;
      this.bridge.emit("NAVIGATE", { state, fromHistory: true });
    });

    window.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.errorOverlay && !this.errorOverlay.hidden) {
        this.hideLoadError();
      }
    });

    if (this.contactForm) {
      this.contactForm.addEventListener("submit", (event) => {
        event.preventDefault();

        if (!this.contactForm.checkValidity()) {
          this.contactStatus.textContent = "Bitte alle Pflichtfelder korrekt ausfüllen.";
          return;
        }

        const formData = new FormData(this.contactForm);
        const token = formData.get("cf-turnstile-response");

        if (!token) {
          this.contactStatus.textContent = "Bitte zuerst das Captcha ausfüllen.";
          return;
        }

        this.contactStatus.textContent = "Sende Nachricht ...";
        if (this.contactSubmit) {
          this.contactSubmit.disabled = true;
        }
        this.bridge.emit("CONTACT_SUBMIT", { formData });
      });
    }
  }

  setLoaderProgress(value) {
    const clamped = Math.max(0, Math.min(1, value));
    if (this.loaderProgress) {
      this.loaderProgress.style.width = `${Math.round(clamped * 100)}%`;
    }
    if (this.loaderText) {
      this.loaderText.textContent = `${Math.round(clamped * 100)}%`;
    }
  }

  hideLoader() {
    if (!this.loader) {
      return;
    }
    this.loader.classList.add("loader-hidden");
  }

  showLoadError() {
    if (this.errorOverlay) {
      this.errorOverlay.style.display = "";
      this.errorOverlay.hidden = false;
    }
  }

  hideLoadError() {
    if (!this.errorOverlay) {
      return;
    }

    this.errorOverlay.hidden = true;
    this.errorOverlay.style.display = "none";
  }

  setQualityMode({ mode, details }) {
    if (!this.qualityBadge) {
      return;
    }

    const modeLabel = mode || "LIGHT";
    const suffix = details ? ` | ${details}` : "";
    this.qualityBadge.dataset.mode = modeLabel;
    this.qualityBadge.textContent = `MODE: ${modeLabel}${suffix}`;
  }

  onContactResult({ ok, message }) {
    this.contactStatus.textContent = message;
    if (this.contactSubmit) {
      this.contactSubmit.disabled = false;
    }

    if (ok && this.contactForm) {
      this.contactForm.reset();
      if (window.turnstile && typeof window.turnstile.reset === "function") {
        try {
          window.turnstile.reset();
        } catch (_error) {
          // Keep UX functional even if widget reset is unavailable.
        }
      }
    }
  }

  showProjectDetail(slug) {
    const project = projectMap[slug];
    if (!project) {
      return;
    }

    this.projectCategory.textContent = project.category;
    this.projectTitle.textContent = project.title;
    this.projectTeaser.textContent = project.teaser;
    this.projectBody.textContent = project.body;

    if (project.links?.length) {
      this.projectLink.hidden = false;
      this.projectLink.textContent = project.links[0].label;
      this.projectLink.href = project.links[0].href;
    } else {
      this.projectLink.hidden = true;
      this.projectLink.removeAttribute("href");
    }

    this.projectDetail.classList.add("is-open");
    this.projectDetail.setAttribute("aria-hidden", "false");
    this.projectDetail.focus();
  }

  hideProjectDetail() {
    this.projectDetail.classList.remove("is-open");
    this.projectDetail.setAttribute("aria-hidden", "true");
  }

  updateHistory(state, { replace = false, skip = false } = {}) {
    if (skip) {
      return;
    }

    const path = state === SCENE_STATES.LEGAL ? "/new/datenschutz" : "/new/";
    const method = replace ? "replaceState" : "pushState";

    if (window.location.pathname === path) {
      return;
    }

    window.history[method]({ state }, "", path);
  }

  renderState(state, payload = {}, options = {}) {
    this.currentState = state;

    const activePanel = state === SCENE_STATES.PROJECT_DETAIL ? SCENE_STATES.PROJECTS : state;
    this.panels.forEach((panel) => {
      const panelName = panel.dataset.panel;
      panel.classList.toggle("panel-active", panelName === activePanel);
    });

    if (state === SCENE_STATES.PROJECT_DETAIL && payload.slug) {
      this.showProjectDetail(payload.slug);
    } else {
      this.hideProjectDetail();
    }

    document.body.classList.toggle("legal-mode", state === SCENE_STATES.LEGAL);
    this.updateHistory(state, options);
  }
}
